import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

// Reusable hook for IntersectionObserver
const useInView = (ref, threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, threshold]);

  return isVisible;
};

const academicPrograms = [
  {
    id: 1,
    title: "Junior School",
    description:
      "For Grades 7-9, we develop critical thinking and problem-solving skills through interactive and engaging learning methods. Located in Nakuru, our Junior School follows the CBC curriculum to foster intellectual curiosity.",
    icon: "🧠",
    color: "primary"
  },
  {
    id: 2,
    title: "Senior School",
    description:
      "For Grades 10-12, we offer specialized pathways in STEM and Social Sciences to prepare students for their future careers. Our Senior School in Nakuru emphasizes academic excellence and holistic development.",
    icon: "🚀",
    color: "primary"
  }
];

const programLinks = {
  "Kindergarten": "/kindergarten",
  "Primary School": "/pre-primary",
  "Junior School": "/middle-academics",
  "Secondary School": "/secondary-academics",
  "Senior School": "/senior-secondary-academics",
};

const floatingElements = [
  { top: "20%", left: "10%", size: "20", color: "primary/5", delay: "0s" },
  { top: "40%", right: "20%", size: "16", color: "gold/10", delay: "1s" },
  { bottom: "40%", left: "20%", size: "12", color: "primary/10", delay: "2s" },
  { top: "60%", left: "50%", size: "8", color: "gold/15", delay: "0.5s" },
];

const Academics = () => {
  const [headerVisible, setHeaderVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(false);
  const sectionRef = useRef(null);
  const infoVisible = useInView(sectionRef);

  useEffect(() => {
    const timer1 = setTimeout(() => setHeaderVisible(true), 100);
    const timer2 = setTimeout(() => setCardsVisible(true), 300);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <div className="bg-background-light dark:bg-background-dark font-display text-slate-800 dark:text-slate-200 relative overflow-hidden">
      {/* Floating Background Elements */}
      {floatingElements.map((el, i) => (
        <div
          key={i}
          className="absolute rounded-full animate-float"
          style={{
            top: el.top,
            left: el.left,
            right: el.right,
            bottom: el.bottom,
            width: `${el.size}px`,
            height: `${el.size}px`,
            backgroundColor: `var(--tw-${el.color})`,
            animationDelay: el.delay
          }}
        />
      ))}

      <main className="relative z-10 min-h-screen flex flex-col">
        {/* Hero Section */}
        <section className="bg-primary/10 dark:bg-primary/20 py-12 md:py-20 text-center">
          <div className="container mx-auto px-6 max-w-4xl">
            <h1
              className={`text-4xl md:text-5xl font-bold text-primary dark:text-white mb-4 transition-all duration-1000 ease-out transform ${
                headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              Academics Overview
            </h1>
            <p
              className={`text-lg text-slate-600 dark:text-slate-300 transition-all duration-1000 ease-out transform ${
                headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
              style={{ transitionDelay: '300ms' }}
            >
              Explore our comprehensive academic programs aligned with Competence-Based Education (CBC) Curriculum and designed to foster intellectual curiosity and personal growth from Junior School to Grade 12 in Nakuru.
            </p>
          </div>
        </section>

        {/* Academic Programs */}
        <section className="container mx-auto px-6 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-8">
          {academicPrograms.map((program, index) => (
            <article
              key={program.id}
              className={`bg-white dark:bg-slate-800/50 rounded-xl shadow-lg hover:shadow-2xl transition-transform duration-1000 transform hover:-translate-y-2 overflow-hidden ${
                cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${500 + index * 150}ms` }}
              aria-label={`${program.title} program overview`}
            >
              <div className="p-8 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                      program.color === 'primary'
                        ? 'bg-primary/10 group-hover:bg-primary/20'
                        : 'bg-gold/10 group-hover:bg-gold/20'
                    }`}
                  >
                    <span className="text-2xl">{program.icon}</span>
                  </div>
                  <h2 className={`text-2xl font-bold ${program.color === 'primary' ? 'text-primary dark:text-purple-300' : 'text-gold'}`}>
                    {program.title}
                  </h2>
                </div>
                <p className="text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">{program.description}</p>
                <Link
                  to={programLinks[program.title] || "#"}
                  title={`Learn more about ${program.title}`}
                  className="mt-auto w-full inline-flex items-center justify-center font-bold tracking-wide transition-transform duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 transform hover:scale-105 active:scale-95 bg-primary text-white hover:bg-primary/90 focus:ring-primary shadow-lg h-10 px-6 text-sm rounded-full"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  Learn More
                </Link>
                <div
                  className={`h-1 w-full transition-all duration-500 ${
                    program.color === 'primary'
                      ? 'bg-gradient-to-r from-primary to-primary/50'
                      : 'bg-gradient-to-r from-gold to-gold/50'
                  }`}
                />
              </div>
            </article>
          ))}
        </section>

        {/* Additional Information Section */}
        <section ref={sectionRef} className="bg-white dark:bg-slate-800/30 py-16 text-center">
          <div className="container mx-auto px-6 max-w-4xl transition-all duration-1000 ease-out transform" 
               style={{ opacity: infoVisible ? 1 : 0, transform: infoVisible ? 'translateY(0)' : 'translateY(2rem)' }}>
            <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-6 flex items-center justify-center gap-3">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">⭐</span>
              Academic Excellence
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div className="text-center group">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors duration-300">
                  <span className="text-primary text-2xl">📊</span>
                </div>
                <h4 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">CBE Curriculum</h4>
                <p className="text-slate-600 dark:text-slate-300">Offering the Competence-Based Education curriculum for comprehensive education in Nakuru.</p>
              </div>
              <div className="text-center group">
                <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gold/20 transition-colors duration-300">
                  <span className="text-gold text-2xl">👨‍🏫</span>
                </div>
                <h4 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Expert Faculty</h4>
                <p className="text-slate-600 dark:text-slate-300">Experienced and qualified teachers dedicated to student success.</p>
              </div>
              <div className="text-center group">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors duration-300">
                  <span className="text-primary text-2xl">🎯</span>
                </div>
                <h4 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Holistic Development</h4>
                <p className="text-slate-600 dark:text-slate-300">Balancing academics with extracurricular activities and life skills.</p>
              </div>
            </div>
          </div>
        </section>

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            "name": "Anestar Group Of Schools",
            "url": "https://www.anestarjss.com/academics",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Nakuru",
              "addressCountry": "KE"
            },
            "department": academicPrograms.map(p => ({
              "@type": "EducationalOccupationalProgram",
              "name": p.title,
              "educationalLevel": p.title === "Junior School" ? "Grade 7-9" : "Grade 10-12"
            }))
          })}
        </script>
      </main>
    </div>
  );
};

export default Academics;
